import { useState } from 'react';
import {
  categoryMeta, bookingModeMeta, formatMoney,
  type HebergementProperty, type HebergementUnit,
  type HebergementMedia, type HebergementAmenity, type HebergementCategory, type CategoryInput,
} from '@/lib/hebergement';
import PropertyFormModal from './PropertyFormModal';

interface Props {
  properties: HebergementProperty[];
  unitsFor: (propertyId: number) => HebergementUnit[];
  mediaFor: (propertyId: number) => HebergementMedia[];
  amenitiesFor: (propertyId: number) => HebergementAmenity[];
  currency: string;
  categories: HebergementCategory[];
  createCategory: (input: CategoryInput) => Promise<HebergementCategory | null>;
  createProperty: (data: Partial<HebergementProperty>) => Promise<HebergementProperty>;
  updateProperty: (id: number, patch: Partial<HebergementProperty>) => Promise<void>;
  toggleFeatured: (id: number, featured: boolean) => Promise<void>;
  deleteProperty: (id: number) => Promise<void>;
  savePropertyMedia: (propertyId: number, items: { url: string; type: string; caption?: string }[]) => Promise<void>;
  savePropertyAmenities: (propertyId: number, names: string[]) => Promise<void>;
  onAddUnit: (propertyId: number) => void;
}

export default function PropertiesView({
  properties, unitsFor, mediaFor, amenitiesFor, currency, categories, createCategory,
  createProperty, updateProperty, toggleFeatured, deleteProperty,
  savePropertyMedia, savePropertyAmenities, onAddUnit,
}: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<HebergementProperty | null>(null);
  const [deleting, setDeleting] = useState<HebergementProperty | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (p: HebergementProperty) => { setEditing(p); setModalOpen(true); };

  const handleSubmit = async (
    data: Partial<HebergementProperty>,
    mediaItems: { url: string; type: string }[],
    amenityNames: string[],
  ) => {
    let propertyId: number;
    if (editing) {
      await updateProperty(editing.id, data);
      propertyId = editing.id;
    } else {
      const created = await createProperty(data);
      propertyId = created.id;
    }
    await savePropertyMedia(propertyId, mediaItems);
    await savePropertyAmenities(propertyId, amenityNames);
  };

  const togglePublish = async (p: HebergementProperty) => {
    setBusyId(p.id);
    try {
      await updateProperty(p.id, { published: !p.published, status: !p.published ? 'active' : 'draft' });
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusyId(deleting.id);
    try {
      await deleteProperty(deleting.id);
      setDeleting(null);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-foreground-500">
          {properties.length} bien{properties.length > 1 ? 's' : ''} d&apos;hébergement
        </p>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-add-line"></i>Nouveau bien
        </button>
      </div>

      {properties.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-dashed border-background-300/60 rounded-lg text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary-100 text-primary-600 flex items-center justify-center mb-4">
            <i className="ri-building-2-line text-3xl"></i>
          </div>
          <h3 className="text-base font-semibold text-foreground-800 mb-1">Aucun bien pour l&apos;instant</h3>
          <p className="text-sm text-foreground-500 mb-4 max-w-sm">
            Créez votre premier hébergement : villa, appartement, riad, chambre… Vous choisirez ensuite s&apos;il se loue entier ou chambre par chambre.
          </p>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-add-line"></i>Créer un bien
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {properties.map((p) => {
            const meta = categoryMeta(categories, p.type);
            const mode = bookingModeMeta(p.booking_mode);
            const propertyUnits = unitsFor(p.id);
            return (
              <div key={p.id} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden flex flex-col">
                <div className="relative h-40 bg-background-100">
                  {p.cover_image ? (
                    <img src={p.cover_image} alt={p.name} className="w-full h-full object-cover object-top" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-foreground-300">
                      <i className="ri-image-line text-4xl"></i>
                    </div>
                  )}
                  <div className="absolute top-3 left-3 flex gap-1.5">
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-background-50/90 text-foreground-700 backdrop-blur-sm flex items-center gap-1">
                      <i className={meta.icon}></i>{meta.label}
                    </span>
                    {p.published ? (
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-accent-100 text-accent-700 backdrop-blur-sm">Publié</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-background-200/90 text-foreground-600 backdrop-blur-sm">Brouillon</span>
                    )}
                    {p.featured && (
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-accent-500 text-background-50 backdrop-blur-sm flex items-center gap-1">
                        <i className="ri-star-fill text-[10px]"></i>À la une
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => void toggleFeatured(p.id, !p.featured)}
                    disabled={busyId === p.id}
                    title={p.featured ? 'Retirer de la une' : 'Mettre en avant'}
                    aria-label={p.featured ? 'Retirer de la une' : 'Mettre en avant'}
                    className={`absolute top-2.5 right-2.5 z-10 w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer disabled:opacity-50 ${
                      p.featured
                        ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                        : 'bg-background-50/90 text-foreground-500 hover:bg-background-50 hover:text-accent-600'
                    }`}
                  >
                    <i className={`${p.featured ? 'ri-star-fill' : 'ri-star-line'} text-base`}></i>
                  </button>
                </div>

                <div className="p-4 flex flex-col flex-1">
                  <h3 className="text-sm font-semibold text-foreground-900 truncate">{p.name}</h3>
                  <p className="text-xs text-foreground-500 mt-0.5 truncate">
                    <i className="ri-map-pin-line mr-1"></i>
                    {[p.city, p.country].filter(Boolean).join(', ') || 'Localisation non renseignée'}
                  </p>

                  <div className="flex flex-wrap gap-2 mt-3 text-[11px] text-foreground-600">
                    <span className="px-2 py-0.5 rounded-full bg-background-100 flex items-center gap-1"><i className="ri-user-line"></i>{p.capacity}</span>
                    <span className="px-2 py-0.5 rounded-full bg-background-100 flex items-center gap-1"><i className="ri-hotel-bed-line"></i>{p.bed_count} lits</span>
                    <span className="px-2 py-0.5 rounded-full bg-background-100 flex items-center gap-1"><i className="ri-drop-line"></i>{p.bathroom_count} SDB</span>
                  </div>

                  <div className="flex items-center justify-between mt-3">
                    <div className="text-sm font-bold font-heading text-foreground-950">
                      {formatMoney(Number(p.price) || 0, currency)}
                      <span className="text-xs font-normal text-foreground-400"> / nuit</span>
                    </div>
                    <span className="text-[10px] text-foreground-400 truncate max-w-[130px]" title={mode.label}>
                      {p.booking_mode === 'ENTIRE_PROPERTY' ? 'Bien entier' : p.booking_mode === 'UNITS_ONLY' ? 'Chambre par chambre' : 'Entier + chambres'}
                    </span>
                  </div>

                  <div className="mt-3 pt-3 border-t border-background-200/70 flex items-center gap-1.5">
                    <button
                      onClick={() => onAddUnit(p.id)}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-door-open-line"></i>
                      {propertyUnits.length > 0 ? `${propertyUnits.length} unité${propertyUnits.length > 1 ? 's' : ''}` : 'Ajouter unités'}
                    </button>
                    <button
                      onClick={() => togglePublish(p)}
                      disabled={busyId === p.id}
                      className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 ${
                        p.published ? 'bg-background-100 text-foreground-600 hover:bg-background-200/70' : 'bg-accent-100 text-accent-700 hover:bg-accent-200'
                      }`}
                    >
                      <i className={p.published ? 'ri-eye-off-line' : 'ri-global-line'}></i>
                      {p.published ? 'Dépublier' : 'Publier'}
                    </button>
                    <div className="ml-auto flex items-center gap-1">
                      <button onClick={() => openEdit(p)} title="Modifier"
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 transition-colors cursor-pointer">
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                      <button onClick={() => setDeleting(p)} title="Supprimer"
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer">
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <PropertyFormModal
          initial={editing}
          initialMedia={editing ? mediaFor(editing.id).map((m) => ({ url: m.url, type: m.type, caption: m.caption })) : []}
          initialAmenities={editing ? amenitiesFor(editing.id).map((a) => a.name) : []}
          currency={currency}
          categories={categories}
          onCreateCategory={createCategory}
          onClose={() => setModalOpen(false)}
          onSubmit={handleSubmit}
        />
      )}

      {deleting && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[70]" onClick={() => setDeleting(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] w-full max-w-sm bg-background-50 rounded-2xl border border-background-200/70 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-500 text-lg"></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-950">Supprimer « {deleting.name} » ?</h3>
                <p className="text-xs text-foreground-500 mt-0.5">Les unités associées seront aussi supprimées.</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setDeleting(null)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={confirmDelete} disabled={busyId === deleting.id}
                className="px-5 py-2 rounded-full text-sm font-semibold bg-red-500 text-white whitespace-nowrap hover:bg-red-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                <i className="ri-delete-bin-line"></i>Supprimer
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}